import { useState, useEffect, useRef } from "react";
import { useAdminBoard } from "@/contexts/AdminBoardContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import {
  Bot,
  Send,
  Sparkles,
  MessageSquare,
  Loader2,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  FileText,
  RefreshCw,
  Clock,
  Zap,
  BarChart3,
  Bell,
  Lightbulb,
  Plus,
  Trash2,
  Download
} from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL;

export default function AdminAICopilot() {
  const { user } = useAdminBoard();
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [anomalies, setAnomalies] = useState([]);
  const [reports, setReports] = useState([]);
  const [insights, setInsights] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [activeTab, setActiveTab] = useState("chat");
  const [generatingReport, setGeneratingReport] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadAllData();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const loadAllData = async () => {
    setLoading(true);
    const token = localStorage.getItem("speakr_admin_token");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const [anomaliesRes, reportsRes, insightsRes, suggestionsRes, sessionsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/copilot/anomalies?days=7`, { headers }),
        fetch(`${API_URL}/api/admin/copilot/reports?limit=5`, { headers }),
        fetch(`${API_URL}/api/admin/copilot/insights`, { headers }),
        fetch(`${API_URL}/api/admin/copilot/suggestions`, { headers }),
        fetch(`${API_URL}/api/admin/copilot/chat/sessions?limit=10`, { headers })
      ]);

      if (anomaliesRes.ok) {
        const data = await anomaliesRes.json();
        setAnomalies(data.anomalies || []);
      }
      if (reportsRes.ok) {
        const data = await reportsRes.json();
        setReports(data.reports || []);
      }
      if (insightsRes.ok) {
        const data = await insightsRes.json();
        setInsights(data.insights || []);
      }
      if (suggestionsRes.ok) {
        const data = await suggestionsRes.json();
        setSuggestions(data.suggestions || []);
      }
      if (sessionsRes.ok) {
        const data = await sessionsRes.json();
        setSessions(data.sessions || []);
      }
    } catch (e) {
      toast.error("Failed to load copilot data");
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (text = input) => {
    if (!text.trim()) return;

    const userMessage = { role: "user", content: text };
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setChatLoading(true);

    const token = localStorage.getItem("speakr_admin_token");
    try {
      const res = await fetch(`${API_URL}/api/admin/copilot/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          message: text,
          session_id: sessionId
        })
      });

      if (res.ok) {
        const data = await res.json();
        setSessionId(data.session_id);
        setMessages(prev => [...prev, { role: "assistant", content: data.response }]);
      } else {
        throw new Error("Failed");
      }
    } catch (e) {
      toast.error("Failed to send message");
      setMessages(prev => [...prev, { role: "assistant", content: "⚠️ I couldn't process your request. Please try again." }]);
    } finally {
      setChatLoading(false);
    }
  };

  const generateReport = async (type) => {
    setGeneratingReport(true);
    const token = localStorage.getItem("speakr_admin_token");

    try {
      const res = await fetch(`${API_URL}/api/admin/copilot/reports/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ report_type: type })
      });

      if (res.ok) {
        toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} report generated`);
        loadAllData();
      } else {
        throw new Error("Failed");
      }
    } catch (e) {
      toast.error("Failed to generate report");
    } finally {
      setGeneratingReport(false);
    }
  };

  const runAnomalyDetection = async () => {
    const token = localStorage.getItem("speakr_admin_token");
    try {
      const res = await fetch(`${API_URL}/api/admin/copilot/anomalies/detect`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Found ${data.count} anomalies`);
        loadAllData();
      }
    } catch (e) {
      toast.error("Failed to run detection");
    }
  };

  const acknowledgeAnomaly = async (anomalyId) => {
    const token = localStorage.getItem("speakr_admin_token");
    try {
      await fetch(`${API_URL}/api/admin/copilot/anomalies/acknowledge`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ anomaly_id: anomalyId })
      });
      toast.success("Anomaly acknowledged");
      loadAllData();
    } catch (e) {
      toast.error("Failed to acknowledge");
    }
  };

  const clearChat = () => {
    setMessages([]);
    setSessionId(null);
  };

  const loadSession = async (sid) => {
    const token = localStorage.getItem("speakr_admin_token");
    try {
      const res = await fetch(`${API_URL}/api/admin/copilot/chat/history/${sid}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSessionId(sid);
        setMessages(data.history.map(h => ({ role: h.role, content: h.content })));
      }
    } catch (e) {
      toast.error("Failed to load session");
    }
  };

  const getSeverityBadge = (severity) => {
    const variants = {
      critical: { class: "bg-red-100 text-red-800", icon: AlertTriangle },
      warning: { class: "bg-amber-100 text-amber-800", icon: AlertTriangle },
      info: { class: "bg-blue-100 text-blue-800", icon: Bell }
    };
    const v = variants[severity] || variants.info;
    const Icon = v.icon;
    return (
      <Badge className={`${v.class} gap-1`}>
        <Icon className="w-3 h-3" />
        {severity}
      </Badge>
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="admin-copilot-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center shadow-lg shadow-purple-500/25">
            <Bot className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">AI Copilot</h1>
            <p className="text-slate-500">Intelligent reporting, anomaly detection & strategic insights</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={runAnomalyDetection} variant="outline" data-testid="run-detection-btn">
            <Zap className="w-4 h-4 mr-2" />
            Run Detection
          </Button>
          <Button onClick={() => generateReport("daily")} disabled={generatingReport} data-testid="generate-report-btn">
            {generatingReport ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
            Generate Report
          </Button>
        </div>
      </div>

      {/* Quick Insights */}
      {insights.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {insights.map((insight, idx) => (
            <div
              key={idx}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border rounded-full shadow-sm"
            >
              <span>{insight.icon}</span>
              <span className="text-sm font-medium">{insight.text}</span>
              {insight.trend === "up" && <TrendingUp className="w-4 h-4 text-emerald-500" />}
              {insight.trend === "down" && <TrendingDown className="w-4 h-4 text-red-500" />}
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chat Panel */}
        <div className="lg:col-span-2">
          <Card className="h-[600px] flex flex-col">
            <CardHeader className="pb-3 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-purple-500" />
                    Chat with Copilot
                  </CardTitle>
                  <CardDescription>Ask questions about your business metrics</CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={clearChat}>
                  <Trash2 className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              </div>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col p-0">
              {/* Messages */}
              <ScrollArea className="flex-1 p-4">
                {messages.length === 0 ? (
                  <div className="space-y-4">
                    <div className="text-center py-8">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500/10 to-blue-600/10 flex items-center justify-center mx-auto mb-4">
                        <Sparkles className="w-8 h-8 text-purple-500" />
                      </div>
                      <h4 className="text-lg font-medium mb-1">How can I help you today?</h4>
                      <p className="text-sm text-slate-500">Ask me about metrics, anomalies, or get strategic advice</p>
                    </div>

                    {/* Suggestions */}
                    <div className="grid grid-cols-2 gap-2">
                      {suggestions.slice(0, 2).map((category, idx) => (
                        category.queries.slice(0, 2).map((query, qIdx) => (
                          <button
                            key={`${idx}-${qIdx}`}
                            onClick={() => sendMessage(query)}
                            className="text-left p-3 rounded-lg bg-slate-50 hover:bg-slate-100 text-sm transition flex items-start gap-2"
                          >
                            <Lightbulb className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                            <span>{query}</span>
                          </button>
                        ))
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((msg, idx) => (
                      <div
                        key={idx}
                        className={`flex gap-3 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                      >
                        {msg.role === "assistant" && (
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                            <Bot className="w-4 h-4 text-white" />
                          </div>
                        )}
                        <div
                          className={`rounded-2xl px-4 py-2 max-w-[80%] ${
                            msg.role === "user"
                              ? "bg-blue-600 text-white"
                              : "bg-slate-100 text-slate-900"
                          }`}
                        >
                          <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
                        </div>
                        {msg.role === "user" && (
                          <div className="w-8 h-8 rounded-lg bg-slate-200 flex items-center justify-center flex-shrink-0">
                            <MessageSquare className="w-4 h-4 text-slate-600" />
                          </div>
                        )}
                      </div>
                    ))}
                    {chatLoading && (
                      <div className="flex gap-3">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center">
                          <Bot className="w-4 h-4 text-white" />
                        </div>
                        <div className="bg-slate-100 rounded-2xl px-4 py-2">
                          <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>

              {/* Input */}
              <div className="p-4 border-t">
                <div className="flex gap-2">
                  <Input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask about revenue, churn, calls, leads..."
                    className="flex-1"
                    disabled={chatLoading}
                    data-testid="copilot-chat-input"
                  />
                  <Button
                    onClick={() => sendMessage()}
                    disabled={!input.trim() || chatLoading}
                    data-testid="copilot-chat-send"
                  >
                    {chatLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Side Panel */}
        <div className="space-y-6">
          {/* Anomalies */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Recent Anomalies
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {anomalies.filter(a => !a.acknowledged).length === 0 ? (
                <div className="text-center py-4 text-sm text-slate-500">
                  <CheckCircle className="w-8 h-8 mx-auto text-emerald-400 mb-2" />
                  No unacknowledged anomalies
                </div>
              ) : (
                anomalies.filter(a => !a.acknowledged).slice(0, 3).map((anomaly) => (
                  <div key={anomaly.id} className="p-3 border rounded-lg space-y-2" data-testid={`anomaly-${anomaly.id}`}>
                    <div className="flex items-start justify-between">
                      <p className="font-medium text-sm">{anomaly.title}</p>
                      {getSeverityBadge(anomaly.severity)}
                    </div>
                    <p className="text-xs text-slate-500">{anomaly.message}</p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => acknowledgeAnomaly(anomaly.id)}
                      className="w-full"
                    >
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Acknowledge
                    </Button>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Recent Reports */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-500" />
                  Recent Reports
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => generateReport("weekly")}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {reports.length === 0 ? (
                <div className="text-center py-4 text-sm text-slate-500">
                  No reports yet
                </div>
              ) : (
                reports.slice(0, 3).map((report) => (
                  <div
                    key={report.id}
                    className="p-2 border rounded-lg hover:bg-slate-50 cursor-pointer flex items-center justify-between"
                  >
                    <div>
                      <p className="text-sm font-medium capitalize">{report.type} Report</p>
                      <p className="text-xs text-slate-500">
                        {new Date(report.generated_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {report.type}
                    </Badge>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Chat Sessions */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-slate-500" />
                Recent Sessions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {sessions.length === 0 ? (
                <div className="text-center py-4 text-sm text-slate-500">
                  No chat history
                </div>
              ) : (
                sessions.slice(0, 5).map((session) => (
                  <button
                    key={session.session_id}
                    onClick={() => loadSession(session.session_id)}
                    className="w-full p-2 border rounded-lg hover:bg-slate-50 text-left"
                  >
                    <p className="text-sm truncate">{session.last_message}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {session.message_count} messages
                    </p>
                  </button>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
